import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { cn } from '../../lib/utils';

interface NavRowProps {
  icon: ReactNode;
  label: string;
  to?: string;
  onClick?: () => void;
  active?: boolean;
  trailing?: ReactNode;
  className?: string;
  'aria-label'?: string;
}

const ROW_CLASSES =
  'flex h-9 w-full items-center gap-2.5 rounded-xl pl-4 pr-2 text-left transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/70 [&>svg]:size-4 [&>svg]:shrink-0';
const INACTIVE_CLASSES =
  'font-medium text-seeker-400 hover:bg-seeker-900 hover:text-white';
const ACTIVE_CLASSES =
  'bg-seeker-900 font-bold text-white shadow-[inset_0_0.375px_0.375px_0_rgba(255,255,255,0.17)]';

/** 36px sidebar row (Figma "Navigation" item). Renders a Link when `to` is set. */
export function NavRow({
  icon,
  label,
  to,
  onClick,
  active = false,
  trailing,
  className,
  'aria-label': ariaLabel,
}: NavRowProps) {
  const classes = cn(
    ROW_CLASSES,
    active ? ACTIVE_CLASSES : INACTIVE_CLASSES,
    className,
  );
  const content = (
    <>
      {icon}
      <span className="min-w-0 flex-1 truncate text-sm leading-5">{label}</span>
      {trailing}
    </>
  );

  if (to) {
    return (
      <Link
        to={to}
        onClick={onClick}
        aria-label={ariaLabel}
        aria-current={active ? 'page' : undefined}
        className={classes}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className={classes}
    >
      {content}
    </button>
  );
}
